import React, { useEffect, useState } from "react";
import { TextField, Autocomplete, Box, Grid, Skeleton } from "@mui/material";
import Pagelayout from "../components/Pagelayout";
import { useLocation, useNavigate } from "react-router-dom";
import CustomTable from "../components/ListPageComponent";
import {
  getCurrentDate,
  toSystemDate,
} from "../../../../util/utilityFunctions";
import {
  batchPayslipSingleView,
  computeBatchPayslip,
  deleteBatchPayslip,
  departmentListApi,
  fetchMultipleAPIs,
  getAllBranchesAPI,
  postBatchPayslip,
  saveBatchPayslip,
  updateBatchPayslip,
} from "../parollAPIs";
import { ErrorDisplay } from "../configuration/settings/PayrollSettings";

const CreateBatchPayslip = () => {
  const location = useLocation();
  const singleViewId =
    location?.state?._id || localStorage.getItem("singleViewId") || null;

  const navigate = useNavigate();
  const [listData, setListData] = useState(null);

  const [errorMsg, setErrorMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [singleViewData, setSingleViewData] = useState(false);
  const [edit, setEdit] = useState(false);

  const [formData, setFormData] = useState({
    batchName: null,
    fromDate: getCurrentDate(),
    toDate: getCurrentDate(30),
    branch: null,
    department: null,
    status: "NEW",
    isSingleView: false,
    _id: null,
    paySlipDetails: [],
  });
  // Add error state for validation
  const [errors, setErrors] = useState({
    batchName: false,
    fromDate: false,
    toDate: false,
    branch: false,
    department: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Validation function
  const validateForm = () => {
    const newErrors = {
      batchName: !formData.batchName,
      fromDate: !formData.fromDate,
      toDate: !formData.toDate,
      branch: !formData.branch,
      department: !formData.department,
    };

    setErrors(newErrors);

    // Return true if form is valid (no errors), false otherwise
    return !Object.values(newErrors).some((error) => error === true);
  };

  const handleDeleteRow = (rowData) => {
    console.log("🗑️ Delete called with rowData:", rowData);
    // This is deleting individual payslips from a batch, not the batch itself
    // We need to check if this is batch deletion or payslip deletion
    if (rowData._id) {
      // If this has employeeName, it's a payslip, not a batch
      if (rowData.employeeName) {
        console.log("📄 This is a payslip, not a batch. Payslip ID:", rowData._id);
        // TODO: Implement payslip deletion (not batch deletion)
        setSnackbar({
          open: true,
          message: "Payslip deletion not implemented yet",
          severity: "info",
        });
        return;
      }
      
      // Otherwise, treat as batch deletion
      console.log("📦 Deleting batch with ID:", rowData._id);
      deleteBatchPayslip({
        id: rowData?._id,
        successCallback: (data) => {
          console.log("Deleted successfully:", data);
          setSnackbar({
            open: true,
            message: `${rowData?.batchName || 'Batch'} has been deleted`,
            severity: "success",
          });
          // Refresh the page or navigate back
          window.location.reload();
        },
        errorCallback: (error) => {
          console.log("Error deleting:", error);
          setSnackbar({
            open: true,
            message: "Failed to delete batch",
            severity: "error",
          });
        },
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    setErrors({ ...errors, [name]: !value });
  };

  const handleAutocompleteChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user selects a value
    setErrors({ ...errors, [field]: !value });
  };

  const handleComputePayslip = () => {
    if (formData.isSingleView) {
      // Compute payslip
      computeBatchPayslip({
        body: { _id: formData?._id },
        successCallback: (data) => {
          console.log("Computed:", data);
          batchPayslipSingleView({
            id: data._id,
            successCallback: (data) => {
              console.log("Success:", data);
            
              setSnackbar({
                open: true,
                message: `Batch payslip has been computed`,
                severity: "success",
              });

              batchPayslipSingleView({
                id: singleViewId,
                successCallback: (data) => {
                  console.log("Success:", data);
                  setFormData((prev) => ({
                    ...prev,
                    paySlipDetails: data?.payslipdetails,
                    status: data.status
                  }));
                },
                errorCallback: (error) => {
                  console.log("Error:", error)
                  setSnackbar({
                    open: true,
                    message: error.message,
                    severity: "error",
                  });
                },
              });
            },

            errorCallback: (error) => {
              console.log("Error:", error);
              setSnackbar({
                open: true,
                message: error.message,
                severity: "error",
              });
            },
          });
        },
        errorCallback: (error) => console.log("Error:", error),
      });
    }
  };

  // const handleResetToDraft = () => {};

  // const handleGeneratePayslip = () => {
  //   setFormData({ ...formData, generatePayslip: true });
  // };

  //handle save
  const handleSave = () => {
    if (validateForm()) {
      // console.log("Form data:", formData);

      if (!formData.isSingleView) {
        saveBatchPayslip({
          body: formData,
          successCallback: (data) => {
            // console.log("Success:", data)
            localStorage.setItem("singleViewId", data._id);
            setFormData((prev) => ({
              ...prev,
              _id: data._id,
              isSingleView: true,
              status: data.status,
            }));
            setSnackbar({
              open: true,
              message: "Batch payslip has been created",
              severity: "success",
            });
            setEdit(!edit);
          },
          errorCallback: (error) => {
            console.log("Error:", error);
            setSnackbar({
              open: true,
              message: error.message,
              severity: "error",
            });
          },
        });
      } else {
        // Update existing batch payslip
        if (formData._id) {
          updateBatchPayslip({
            body: formData,
            successCallback: (data) => {
              console.log("Updated:", data);
              setSnackbar({
                open: true,
                message: "Batch payslip has been updated",
                severity: "success",
              });
              setEdit(!edit);
            },
            errorCallback: (error) => {
              console.log("Error:", error);
              setSnackbar({
                open: true,
                message: error.message,
                severity: "error",
              });
            },
          });
        }
      }
    } else {
      console.log("Form has validation errors");
    }
  };

  //handle post
  const handlePostBtn = () => {
    if (formData.isSingleView) {
      postBatchPayslip({
        body: { _id: formData?._id },
        successCallback: (data) => {
          console.log("Posted:", data);
          setSnackbar({
            open: true,
            message: "Batch payslip has been posted",
            severity: "success",
          });
          setFormData((prev) => ({ ...prev, status: "DONE" }));
        },
        errorCallback: (error) => {
          console.log("Error:", error);
          setSnackbar({
            open: true,
            message: error.message,
            severity: "error",
          });
        },
      });
    }
  };

  const headers = [
    { key: "employeeName", label: "Name" },
    { key: "yearlyCtc", label: "Basic Salary" },
    { key: "totalEarnings", label: "Total allowance" },
    { key: "totalDeductions", label: "Total Deduction " },
    { key: "otherAllowance", label: "Other  allowance" },
  ];

  const visibleColumns = [
    "employeeName",
    "yearlyCtc",
    "totalEarnings",
    "totalDeductions",
    "otherAllowance",
  ];

  useEffect(() => {
    const apiCallsWithNames = [
      {
        name: "branch",
        apiCall: getAllBranchesAPI({
          successCallback: () => {},
          errorCallback: () => {},
        }),
      },
      {
        name: "department",
        apiCall: departmentListApi({
          successCallback: () => {},
          errorCallback: () => {},
        }),
      },
    ];

    fetchMultipleAPIs({
      apiCallsWithNames,
      setLoading,
      setErrorMsg,
      onSuccess: (res) => {
        setListData(res.data);
      },
      onError: (error) => {
        console.error("Error:", error);
      },
    });
  }, []);

  //if single view
  useEffect(() => {
    if (singleViewId) {
      setLoading(true);
      batchPayslipSingleView({
        id: singleViewId,
        successCallback: (data) => {
          console.log("Success:", data);

          setFormData({
            batchName: data?.batchName || null,
            fromDate: toSystemDate(data?.fromDate) || null,
            toDate: toSystemDate(data?.toDate) || null,
            branch:
              listData?.branch?.find((r) => r._id === data?.branchId) || null,
            department:
              listData?.department?.find((r) => r._id === data?.department) ||
              null,
            status: data?.status || "NEW",
            isSingleView: true,
            _id: data?._id || null,
            paySlipDetails: data?.payslipdetails || [],
          });
          setEdit(true);
          setLoading(false);
        },
        errorCallback: (error) => {
          console.log("Error:", error);
          setSnackbar({
            open: true,
            message: error.message,
            severity: "error",
          });
        },
      });
    }
  }, [listData]);

  const handleIsEdit = () => {
    setEdit(!edit);
  };

  const handleRetry = async () => {
    setLoading(true);
    setErrorMsg({ message: "" });
    batchPayslipSingleView({
      id: singleViewData,
      successCallback: (data) => console.log("Success:", data),
      errorCallback: (error) => console.log("Error:", error),
    });
  };

  const handleBack = () => {
    // Navigate back or to a safe fallback page
    window.history.back();
  };

  if (errorMsg) {
    return (
      <ErrorDisplay
        message={`We couldn't load the payroll batch play slip. Please try again or contact support if the problem persists.`}
        onRetry={handleRetry}
        onBack={handleBack}
      />
    );
  }
  return (
    <Pagelayout
      handleBack={() => navigate("/userdashboard/payroll/batchpayslip")}
      // generatePayslip={formData.status === "POST"}
      // handleGeneratePayslip={handleGeneratePayslip}
      headerLayout={2}
      newData={formData.isSingleView === false || !edit}
      actionBtn={formData.status !== "POSTED"}
      status={formData.status}
      computePayslip={formData.status === "DRAFT" && edit}
      // returnToDraft={formData.status === "POST"}
      handleComputePayslip={handleComputePayslip}
      // handleResetToDraft={handleResetToDraft}
      handleSave={handleSave}
      snackbar={snackbar}
      setSnackbar={setSnackbar}
      handleEdit={handleIsEdit}
      postBtn={formData.status === "COMPUTED"}
      editBtn={formData.status === "NEW" || formData.status === "DRAFT"}
      handlePost={handlePostBtn}
      isLoading={loading}
    >
      {loading ? (
        <Box display="flex" flexDirection="column" gap={2} width="100%" m="auto">
          <Grid container spacing={2}>
            {/* Skeleton for form fields */}
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
          </Grid>
          {/* Skeleton for table */}
          {formData.paySlipDetails.length > 0 && (
            <Box pt={5}>
              <Skeleton variant="rectangular" height={200} />
            </Box>
          )}
        </Box>
      ) :
        <>
          <Box display="flex" flexDirection="column" gap={2} width="100%" m="auto">
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Batch Name"
                  name="batchName"
                  value={formData.batchName || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={errors.batchName}
                  helperText={errors.batchName ? "Batch Name is required" : ""}
                  disabled={
                    formData.status === "DONE" ||
                    formData.status === "POSTED" ||
                    formData.status === "COMPUTED" ||
                    (formData.status === "DRAFT" && edit)
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="From Date"
                  name="fromDate"
                  type="date"
                  value={formData.fromDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  error={errors.fromDate}
                  helperText={errors.fromDate ? "From Date is required" : ""}
                  disabled={
                    formData.status === "DONE" ||
                    formData.status === "POSTED" ||
                    formData.status === "COMPUTED" ||
                    (formData.status === "DRAFT" && edit)
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="To Date"
                  name="toDate"
                  type="date"
                  value={formData.toDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  error={errors.toDate}
                  helperText={errors.toDate ? "To Date is required" : ""}
                  disabled={
                    formData.status === "DONE" ||
                    formData.status === "POSTED" ||
                    formData.status === "COMPUTED" ||
                    (formData.status === "DRAFT" && edit)
                  }
                />
              </Grid>

              <Grid item xs={4}>
                <Autocomplete
                  options={listData?.branch || []}
                  value={formData.branch}
                  onChange={(e, value) => handleAutocompleteChange("branch", value)}
                  getOptionLabel={(option) => option.branchName || ""}
                  getOptionKey={(option) => option._id || option.branchName}
                  disabled={
                    formData.status === "DONE" ||
                    formData.status === "POSTED" ||
                    formData.status === "COMPUTED" ||
                    (formData.status === "DRAFT" && edit)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Branch"
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={errors.branch}
                      helperText={errors.branch ? "Branch is required" : ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  options={listData?.department || []}
                  value={formData.department}
                  onChange={(e, value) =>
                    handleAutocompleteChange("department", value)
                  }
                  getOptionLabel={(option) => option?.departmentName || ""}
                  getOptionKey={(option) => option._id || option.departmentName}
                  disabled={
                    formData.status === "DONE" ||
                    formData.status === "POSTED" ||
                    formData.status === "COMPUTED" ||
                    (formData.status === "DRAFT" && edit)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Department"
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={errors.department}
                      helperText={errors.department ? "Department is required" : ""}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
          {formData.paySlipDetails.length > 0 && (
            <Box pt={5}>
              <CustomTable
                headers={headers}
                data={formData?.paySlipDetails}
                visibleColumns={visibleColumns}
                action={formData.status !== "DONE"}
                editBtn={false}
                onDelete={handleDeleteRow}
                loading={false}
              />
            </Box>
          )}
        </>
      }
     
    </Pagelayout>
  );
};

export default CreateBatchPayslip;
